/**
 * DANISH DATES, IN ONE PLACE
 *
 * These four lived in `app/begivenheder/[id]/page.tsx`, and the calendar had
 * its own copy of the month list (capitalised) and of the time formatter
 * (inline). Two copies of "how Theta writes a date" is how two surfaces drift
 * a day apart at a month boundary.
 *
 * NO `"use client"`: the event route's `generateMetadata` runs on the server
 * and writes the same date into the link preview, so this module has to be
 * importable from both sides. It touches nothing but `Date` and `Intl`.
 *
 * THE MONTH NAMES ARE LOWERCASE, because that is Danish orthography inside a
 * sentence ("fredag d. 12. september"). A heading that wants "September 2026"
 * capitalises it with CSS (`capitalize`) rather than keeping a second array.
 *
 * THE ZONE IS THE READER'S, EXCEPT WHERE THERE IS NO READER. Both formatters
 * default to the local zone — right in a browser, where the person looking at
 * the page is the one the time is for. On the server there is nobody to ask
 * and the machine is very likely on UTC, which would put a 00:30 event on the
 * previous day in a link preview, so the server passes `THETA_TIME_ZONE`
 * explicitly. That is the whole reason these take a zone at all.
 *
 * AND "EXCEPT WHERE THERE IS NO READER" NOW INCLUDES ANYTHING THAT RENDERS ON
 * BOTH SIDES. A component that is server-rendered AND hydrated formats the
 * same instant twice, once with the machine's zone and once with the reader's,
 * and React treats the difference as a text mismatch — so the first paint
 * shows a time two hours out and then silently corrects itself. Any such
 * caller must pass the zone explicitly, which makes the string a fact about
 * Theta rather than about whoever is rendering it. `PollDisplay` is the first
 * one (OSO-673 put `PostCard` on the server); `TimeAgo` is the deliberate
 * exception, because its buckets are coarse and approximate.
 */

/**
 * Theta's zone. Used wherever the string must not depend on who is rendering
 * it — either because there is no reader (a link preview built on the server)
 * or because there are two (a component that renders on both sides).
 *
 * It was `EVENT_TIME_ZONE` while the event page was the only caller.
 */
export const THETA_TIME_ZONE = "Europe/Copenhagen";

export const MONTH_LONG = [
  "januar", "februar", "marts", "april", "maj", "juni",
  "juli", "august", "september", "oktober", "november", "december",
];

/** Indexed by day-of-week, so Sunday is 0. */
export const DAY_NAMES = [
  "søndag", "mandag", "tirsdag", "onsdag", "torsdag", "fredag", "lørdag",
];

/** The calendar date this instant falls on, in `timeZone` (local if absent). */
function zonedDate(iso: string, timeZone?: string) {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone,
    year: "numeric",
    month: "numeric",
    day: "numeric",
  }).formatToParts(new Date(iso));
  const at = (type: string) => Number(parts.find((p) => p.type === type)!.value);

  const year = at("year");
  const month = at("month");
  const day = at("day");
  // The weekday OF THAT CALENDAR DATE, which is what "fredag" means here —
  // re-derived from the zoned y/m/d rather than read off the instant, since
  // the instant's own weekday is a fact about the machine's zone.
  const weekday = new Date(Date.UTC(year, month - 1, day)).getUTCDay();
  return { year, month, day, weekday };
}

/** "fredag d. 12. september 2026" — the date line on an event. */
export function formatFullDate(iso: string, timeZone?: string): string {
  const { year, month, day, weekday } = zonedDate(iso, timeZone);
  return `${DAY_NAMES[weekday]} d. ${day}. ${MONTH_LONG[month - 1]} ${year}`;
}

/**
 * "12. september kl. 17.00" — day, month and clock on ONE line, for a place
 * that has room for a date but not for `formatFullDate`'s weekday and year: a
 * link-preview eyebrow, a search result's meta line.
 *
 * It returns `""` rather than throwing on a value `Intl` will not take, because
 * every caller is a subtitle beside a title that is still worth rendering.
 */
export function formatShortDateTime(iso: string, timeZone?: string): string {
  try {
    return new Intl.DateTimeFormat("da-DK", {
      day: "numeric",
      month: "long",
      hour: "2-digit",
      minute: "2-digit",
      timeZone,
    }).format(new Date(iso));
  } catch {
    return "";
  }
}

/** "17.00" — the Danish 24-hour clock. */
export function formatTime(iso: string, timeZone?: string): string {
  return new Date(iso).toLocaleTimeString("da-DK", {
    hour: "2-digit",
    minute: "2-digit",
    timeZone,
  });
}
